import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Helmet } from "react-helmet";

const blogContent: Record<string, {
  title: string;
  date: string;
  readTime: string;
  category: string;
  metaDescription: string;
  keywords: string;
  content: JSX.Element;
}> = {
  "how-to-build-successful-bouldering-gym": {
    title: "How to Build a Successful Bouldering Gym: Complete Guide 2024",
    date: "2024-01-15",
    readTime: "8 min read",
    category: "Construction",
    metaDescription: "Complete guide to building a successful bouldering gym in 2024. Learn about planning, design, construction costs, safety standards, and equipment from industry experts.",
    keywords: "bouldering gym construction, how to build climbing gym, bouldering facility planning, climbing gym costs, indoor climbing wall construction",
    content: (
      <>
        <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
          Building a bouldering gym is a significant investment that requires careful planning, 
          expert execution, and deep understanding of both construction and climbing. With over 
          a decade of experience constructing facilities across Europe, we've learned what makes 
          a bouldering gym truly successful.
        </p>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Planning Your Bouldering Gym</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          The foundation of any successful climbing facility starts with comprehensive planning. 
          Location analysis, target market research, and financial projections are critical first steps. 
          Consider accessibility, parking, ceiling height (minimum 4-4.5 meters), and local competition.
        </p>

        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Key Planning Considerations</h3>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6 ml-4">
          <li><strong className="text-foreground">Space Requirements:</strong> Minimum 400-500 m² for viable facility, 700-1200 m² for competitive gym</li>
          <li><strong className="text-foreground">Ceiling Height:</strong> 4-4.5 meters minimum for bouldering walls</li>
          <li><strong className="text-foreground">Safety Zones:</strong> Adequate matting coverage and fall zones around all walls</li>
          <li><strong className="text-foreground">Amenities:</strong> Changing rooms, reception, training area, social spaces</li>
        </ul>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Design & Engineering</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Professional design is where functionality meets aesthetics. Modern bouldering gyms 
          feature varied wall angles (vertical, overhang, slab), dynamic routes, and training zones. 
          Our 3D modeling process allows clients to visualize the space before construction begins, 
          ensuring optimal wall placement and flow.
        </p>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Construction & Installation</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Quality construction involves precision manufacturing, certified materials, and expert installation. 
          All climbing structures must meet EU safety standards (EN 12572). At Falkors, we manufacture 
          walls using CNC precision equipment, ensuring structural integrity and safety. Professional 
          installation typically takes 2-4 weeks depending on facility size.
        </p>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Equipment & Holds</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          High-quality climbing holds are essential for route variety and longevity. We manufacture 
          our own holds, offering custom designs and durable materials. Initial hold sets should 
          include diverse shapes, sizes, and textures. Budget approximately €15,000-30,000 for 
          comprehensive hold collection depending on gym size.
        </p>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Cost Breakdown</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Typical bouldering gym construction costs (per square meter):
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6 ml-4">
          <li>Climbing wall construction: €300-500/m²</li>
          <li>Safety matting: €80-150/m²</li>
          <li>Climbing holds: €15,000-30,000 total</li>
          <li>Facility fit-out (reception, changing rooms): €50,000-100,000</li>
          <li>Management software & automation: €5,000-15,000</li>
        </ul>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Turnkey Solutions</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Working with an experienced partner who handles design, construction, equipment supply, 
          and software implementation streamlines the entire process. At Falkors, we provide complete 
          turnkey solutions, eliminating the complexity of coordinating multiple contractors and ensuring 
          seamless project execution from concept to grand opening.
        </p>

        <div className="bg-primary/10 border-l-4 border-primary p-6 mt-12 mb-8">
          <h3 className="text-xl font-display text-foreground mb-3">Ready to Start Your Project?</h3>
          <p className="text-muted-foreground mb-4">
            Let's discuss your bouldering gym vision. We'll help you navigate every step from 
            initial planning to successful opening.
          </p>
          <Link to="/#contact">
            <Button className="bg-primary hover:bg-primary-dark text-primary-foreground">
              Contact Our Team
            </Button>
          </Link>
        </div>
      </>
    )
  },
  
  "climbing-holds-guide-types-materials": {
    title: "Climbing Holds Guide: Types, Materials & Selection for Your Gym",
    date: "2024-01-10",
    readTime: "6 min read",
    category: "Equipment",
    metaDescription: "Complete guide to climbing holds: types, materials, durability, and selection tips. Expert advice from hold manufacturers for gym owners and route setters.",
    keywords: "climbing holds, bouldering holds, climbing hold types, polyurethane holds, route setting equipment, climbing gym equipment",
    content: (
      <>
        <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
          Climbing holds are the foundation of any climbing gym's route setting capability. 
          As manufacturers with years of production experience, we understand what makes 
          quality holds and how to select the right equipment for your facility.
        </p>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Types of Climbing Holds</h2>
        
        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Jugs</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Large, positive holds perfect for beginners and rest positions. Essential for warm-up 
          routes and recovery points on challenging problems. Every gym needs a comprehensive 
          jug collection for route variety.
        </p>

        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Crimps</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Small edge holds requiring finger strength. Critical for advanced routes and training. 
          Various depths (mini, small, medium) allow precise difficulty gradation.
        </p>

        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Slopers</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Rounded holds emphasizing friction and body positioning. Essential for modern route 
          setting and technical climbing. Shapes range from gentle domes to steep waves.
        </p>

        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Pinches</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Requires thumb-finger compression grip. Excellent for powerful moves and coordination 
          training. Various widths accommodate different hand sizes and strength levels.
        </p>

        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Volumes</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Large geometric shapes attached to walls, creating varied terrain. Modern gyms 
          extensively use volumes for dynamic wall features and 3D route setting. Essential 
          for contemporary facility aesthetics.
        </p>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Materials & Manufacturing</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Professional climbing holds are manufactured from polyurethane resin, offering optimal 
          balance of durability, friction, and safety. Quality manufacturing ensures:
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6 ml-4">
          <li><strong className="text-foreground">Durability:</strong> Resistant to wear from thousands of climber contacts</li>
          <li><strong className="text-foreground">Consistent Texture:</strong> Reliable friction characteristics across hold set</li>
          <li><strong className="text-foreground">Color Stability:</strong> UV-resistant pigments prevent fading</li>
          <li><strong className="text-foreground">Structural Integrity:</strong> No hollow spots or weak points</li>
          <li><strong className="text-foreground">Safety:</strong> Smooth edges, no sharp points, proper insert placement</li>
        </ul>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Selection Guidelines for Gym Owners</h2>
        
        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Initial Hold Investment</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          For a 500-700 m² facility, budget €20,000-30,000 for comprehensive initial hold set. 
          This ensures sufficient variety for multiple difficulty grades and diverse route styles.
        </p>

        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Hold Rotation & Expansion</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Plan annual budget of €5,000-10,000 for new holds. Regular rotation keeps routes fresh 
          and maintains member engagement. New hold shapes inspire creative route setting.
        </p>

        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Custom vs. Standard Sets</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Standard hold sets offer cost-effective variety. Custom shapes provide unique gym 
          identity and specific training features. We manufacture both, helping gyms balance 
          budget and differentiation.
        </p>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Route Setting Best Practices</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Quality holds enable creative route setting. Professional setters consider hold 
          orientation, spacing, and combinations to create engaging problems. Diverse hold 
          library supports varied movement styles from powerful dynos to technical balance problems.
        </p>

        <div className="bg-primary/10 border-l-4 border-primary p-6 mt-12 mb-8">
          <h3 className="text-xl font-display text-foreground mb-3">Need Hold Supply for Your Gym?</h3>
          <p className="text-muted-foreground mb-4">
            We manufacture high-quality climbing holds and provide custom solutions for gyms 
            across Europe. Let's discuss your hold requirements.
          </p>
          <Link to="/#contact">
            <Button className="bg-primary hover:bg-primary-dark text-primary-foreground">
              Request Catalog
            </Button>
          </Link>
        </div>
      </>
    )
  },
  
  "climbing-gym-management-software-2024": {
    title: "Best Climbing Gym Management Software Solutions in 2024",
    date: "2024-01-05",
    readTime: "7 min read",
    category: "Technology",
    metaDescription: "Comprehensive guide to climbing gym management software: booking systems, membership management, access control, and automation solutions for modern facilities.",
    keywords: "climbing gym software, gym management system, booking software, membership management, gym automation, turnstile system",
    content: (
      <>
        <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
          Modern climbing gyms require sophisticated software to manage operations efficiently, 
          deliver excellent member experience, and maximize revenue. As gym operators ourselves, 
          we understand the critical importance of reliable management systems.
        </p>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Core Software Requirements</h2>
        
        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Membership Management</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Comprehensive member database tracking subscriptions, visits, payments, and preferences. 
          Automated billing, expiration notifications, and membership tier management streamline 
          administrative workload. Mobile app integration allows members to manage their accounts 
          independently.
        </p>

        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Booking & Scheduling</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Online booking system for classes, training sessions, and capacity management. 
          Real-time availability, automated confirmations, and waitlist management improve 
          customer experience. Integration with website and mobile apps essential for modern 
          operations.
        </p>

        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Point of Sale (POS)</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Integrated payment processing for day passes, equipment rental, merchandise, and café 
          sales. Quick transaction processing, multiple payment methods, and receipt management 
          streamline front desk operations.
        </p>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Access Control & Automation</h2>
        
        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Turnstile Systems</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Automated entry systems reduce staffing requirements and improve security. Modern 
          turnstiles integrate with membership software, allowing contactless entry via RFID 
          cards or mobile apps. Real-time capacity monitoring prevents overcrowding.
        </p>

        <h3 className="text-2xl font-display text-foreground mb-3 mt-8">Smart Lockers</h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Electronic locker systems with PIN or RFID access enhance member convenience. 
          Automated assignment, time tracking, and usage analytics optimize locker management.
        </p>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Analytics & Reporting</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Data-driven decision making requires comprehensive analytics:
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6 ml-4">
          <li><strong className="text-foreground">Visit Patterns:</strong> Peak hours, capacity utilization, seasonal trends</li>
          <li><strong className="text-foreground">Revenue Analysis:</strong> Membership income, retail sales, class attendance</li>
          <li><strong className="text-foreground">Member Retention:</strong> Churn rates, engagement metrics, lifetime value</li>
          <li><strong className="text-foreground">Operational Efficiency:</strong> Staff scheduling, resource utilization</li>
        </ul>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Mobile Applications</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Custom mobile apps enhance member engagement and operational efficiency. Essential 
          features include booking management, digital membership cards, route information, 
          progress tracking, and community features. Push notifications for announcements, 
          new routes, and special events keep members engaged.
        </p>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Integration Considerations</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Modern gym software should integrate seamlessly with:
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-6 ml-4">
          <li>Website and online booking portals</li>
          <li>Payment processors and accounting systems</li>
          <li>Email marketing platforms</li>
          <li>Access control hardware (turnstiles, lockers)</li>
          <li>Security systems and cameras</li>
        </ul>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Implementation & Training</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Successful software implementation requires comprehensive staff training, data migration 
          from legacy systems, and member onboarding. We provide complete support throughout 
          implementation, ensuring smooth transition and minimal operational disruption.
        </p>

        <h2 className="text-3xl font-display text-foreground mb-4 mt-12">Cost Considerations</h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Budget €5,000-15,000 for initial software setup including hardware (turnstiles, tablets, 
          RFID readers). Monthly subscription costs typically €200-500 depending on facility size 
          and feature requirements. ROI through reduced staffing needs and improved operational 
          efficiency typically achieved within 12-18 months.
        </p>

        <div className="bg-primary/10 border-l-4 border-primary p-6 mt-12 mb-8">
          <h3 className="text-xl font-display text-foreground mb-3">Turnkey Software Solutions</h3>
          <p className="text-muted-foreground mb-4">
            We provide complete gym management software implementation as part of our turnkey 
            construction packages. Let's discuss how automation can streamline your operations.
          </p>
          <Link to="/#contact">
            <Button className="bg-primary hover:bg-primary-dark text-primary-foreground">
              Learn More
            </Button>
          </Link>
        </div>
      </>
    )
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = slug ? blogContent[slug] : null;

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="text-4xl font-display mb-4">Post Not Found</h1>
          <Link to="/blog">
            <Button>Back to Blog</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | Falkors Group Blog</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.keywords} />
        <link rel="canonical" href={`https://falkors.com/blog/${slug}`} />
        
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content="Falkors Group" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.metaDescription,
            "datePublished": post.date,
            "author": {
              "@type": "Organization",
              "name": "Falkors Group"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Falkors Group",
              "url": "https://falkors.com"
            }
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <article className="pt-32 pb-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            {/* Back Button */}
            <Link to="/blog">
              <Button variant="ghost" className="mb-8 group">
                <ArrowLeft className="mr-2 w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Back to Blog
              </Button>
            </Link>
            
            {/* Header */}
            <header className="mb-12">
              <Badge className="mb-4 bg-primary/10 text-primary">{post.category}</Badge>
              <h1 className="text-4xl sm:text-5xl font-display text-foreground mb-6 leading-tight">
                {post.title}
              </h1>
              
              <div className="flex items-center gap-6 text-muted-foreground">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {post.readTime}
                </span>
              </div>
            </header>
            
            {/* Content */}
            <div className="prose prose-lg max-w-none">
              {post.content}
            </div>
          </div>
        </article>
        
        <Footer />
      </div>
    </>
  );
};

export default BlogPost;